import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useConfirmSubscription, usePlanOffer, useStartSubscription } from '@/hooks/useBillingQueries';
import { useLogout } from '@/hooks/useAuthQueries';
import { Loader2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const CONFIRM_ATTEMPTS = 5;
const CONFIRM_RETRY_MS = 2000;

function formatAud(value: string): string {
  return `$${Number(value).toFixed(2)}`;
}

export default function Subscribe() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planOffer = usePlanOffer();
  const startSubscription = useStartSubscription('signup');
  const confirmSubscription = useConfirmSubscription();
  const logoutMutation = useLogout();

  const status = searchParams.get('status');
  const [confirmState, setConfirmState] = useState<'idle' | 'confirming' | 'failed'>(
    status === 'success' ? 'confirming' : 'idle'
  );
  const confirmStarted = useRef(false);

  const confirmWithRetries = async () => {
    setConfirmState('confirming');
    for (let attempt = 0; attempt < CONFIRM_ATTEMPTS; attempt++) {
      try {
        const entitlement = await confirmSubscription.mutateAsync();
        if (entitlement.subscriptionActive) {
          navigate('/explore', { replace: true });
          return;
        }
      } catch {
        // fall through to retry
      }
      await new Promise((resolve) => setTimeout(resolve, CONFIRM_RETRY_MS));
    }
    setConfirmState('failed');
  };

  useEffect(() => {
    if (status === 'success' && !confirmStarted.current) {
      confirmStarted.current = true;
      void confirmWithRetries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const offer = planOffer.data;
  const cancelled = status === 'cancel';

  if (confirmState !== 'idle') {
    return (
      <div className="flex justify-center items-start w-full overflow-y-auto py-10">
        <Card className="w-full max-w-lg">
          <CardHeader>
            <CardTitle>Finishing your signup</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {confirmState === 'confirming' ? (
              <p data-testid="subscribe-confirming" className="flex items-center text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Confirming your subscription...
              </p>
            ) : (
              <div className="space-y-2">
                <p data-testid="subscribe-confirm-failed" className="text-sm text-destructive">
                  We couldn't confirm your subscription yet. If you completed payment, it may take a
                  moment to register.
                </p>
                <Button data-testid="subscribe-confirm-retry" onClick={() => void confirmWithRetries()}>
                  Check again
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-start w-full overflow-y-auto py-10">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle>Reactivate your subscription</CardTitle>
          <CardDescription>
            Your subscription is not active. Vivport is a monthly subscription; AI usage is pay as
            you go from your usage wallet.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {cancelled && (
            <p data-testid="subscribe-cancelled" className="text-sm text-muted-foreground">
              Checkout cancelled — a subscription is required to use Vivport.
            </p>
          )}

          <div className="space-y-2">
            <p className="font-medium">Monthly subscription</p>
            {planOffer.isError ? (
              <p data-testid="subscribe-error" className="text-sm text-destructive">
                We couldn't load the subscription price. Please try again shortly.
              </p>
            ) : offer ? (
              <div className="text-sm text-muted-foreground space-y-1">
                {offer.discountPercent > 0 ? (
                  <p data-testid="subscribe-price">
                    <span className="line-through mr-2">{formatAud(offer.priceAud)}/month</span>
                    <span className="text-foreground font-medium">
                      {formatAud(offer.effectiveFirstChargeAud)}/month
                    </span>
                    <span className="ml-2">({offer.discountPercent}% discount applied)</span>
                  </p>
                ) : (
                  <p data-testid="subscribe-price" className="text-foreground font-medium">
                    {formatAud(offer.priceAud)}/month
                  </p>
                )}
                {offer.discountPercent === 100 && (
                  <p data-testid="subscribe-full-discount">
                    Your account has a 100% discount — you won't be charged and no payment method
                    is needed.
                  </p>
                )}
                <p>
                  Your first month includes {formatAud(offer.firstMonthCreditAud)} of
                  AI usage credit. After that, top up your usage wallet as you need it.
                </p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Loading plan details…</p>
            )}
            <Button
              data-testid="subscribe-button"
              onClick={() => startSubscription.mutate()}
              disabled={startSubscription.isPending || planOffer.isLoading || planOffer.isError}
            >
              {startSubscription.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Opening checkout...
                </>
              ) : (
                'Subscribe'
              )}
            </Button>
            {startSubscription.isError && (
              <p data-testid="subscribe-checkout-error" className="text-sm text-destructive">
                We couldn't start checkout. Please try again.
              </p>
            )}
          </div>

          <Button
            data-testid="subscribe-signout"
            variant="link"
            className="text-sm text-muted-foreground hover:text-primary p-0 h-auto"
            onClick={() => logoutMutation.mutate()}
            disabled={logoutMutation.isPending}
          >
            Sign out and finish later
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
